import React from 'react'
import { useRouter } from 'next/router'
import { useNavbarLogin } from '@/lib/useNavbarLogin'
import {
	Box,
	Button,
	Container,
	Flex,
	Image,
	IconButton,
} from '@chakra-ui/react'
import Link from 'next/link'
import NavbarLinks from './NavbarLinks'
import { RiMenu2Fill } from 'react-icons/ri'
import { NAVBAR_PORTAL_ID } from './NavbarPortal'
import posthog from 'posthog-js'
import tenantChrome from '@/lib/tenantChrome'
import LocaleSwitcher from '@/components/LocaleSwitcher'
import { useTranslations } from 'next-intl'

import type { SiteNavigationData } from '@/lib/siteNavigation'

interface NavbarProps {
	data?: any
	siteNavigation?: SiteNavigationData
	onToggle?: () => void
}

const Navbar = ({ data, siteNavigation, onToggle }: NavbarProps) => {
	const login = useNavbarLogin()
	const t = useTranslations('nav')
	const { asPath } = useRouter()

	if (!data) return null

	const { logo, links } = data
	const isSwitcherVisible = siteNavigation?.languageSwitcherVisible ?? true
	const switcherPosition = siteNavigation?.languageSwitcherPosition ?? 'right'

	const handleLogoClick = () => {
		posthog.capture('home_button_header')
	}

	const handleLoginClick = () => {
		posthog.capture('log_in_header')
	}

	// TODO: Replace this with a more robust way to check what kind of page we're on
	// Kept in sync with components/Layout.tsx's identical dashboard-route
	// logic — if this falls behind Layout the authenticated portal pages can
	// render the correct sidebar chrome but still show the public Logo+Login
	// header here, which is exactly what happened for tenant-admin routes.
	const isSponsorPortal =
		asPath.includes('/sponsor') &&
		!asPath.includes('/sponsor/login') &&
		!asPath.includes('/sponsor/forgot-password') &&
		!asPath.includes('/sponsor/reset-password') &&
		!asPath.includes('/sponsor/complete')
	const isTenantAdminPortal =
		asPath.includes('/tenant-admin') && !asPath.includes('/tenant-admin/login')
	const isDashboard =
		(asPath.includes('/dashboard') && !asPath.includes('/dashboard/login')) ||
		asPath.includes('/cms') ||
		isSponsorPortal ||
		isTenantAdminPortal
	const isForm =
		asPath.includes('form') ||
		asPath.includes('/paymentConfirm') ||
		asPath.includes('/fail')
	const isLanding = !isDashboard && !isForm

	return (
		<Flex
			bg='white'
			w='full'
			shadow='base'
			minH={{ base: 12, md: 20 }}
			zIndex={11}
			position='sticky'
			top='0'
		>
			<Container
				// Full width on Dashboard
				maxW={isDashboard ? 'none' : 'container.xl'}
				display='flex'
				minW={0}
				px={4}
			>
				<Flex alignItems='center' gap={{ base: 2, md: 4 }} w='full'>
					{isDashboard && (
						<>
							<IconButton
								aria-label={t('toggleSidebar')}
								variant='ghost'
								size={{ base: 'sm', md: 'md' }}
								display={{ md: 'none' }}
								colorScheme='primary'
								ml={-2}
								onClick={onToggle}
								icon={<RiMenu2Fill />}
							/>
							<Box id={NAVBAR_PORTAL_ID} flex={1} minW={0} />
						</>
					)}
					{!isDashboard && (
						<Box as={Link} href='/' flexShrink={0} onClick={handleLogoClick}>
							<Image src={logo} h={{ base: '6', md: '9' }} m={0} alt='Logo' />
						</Box>
					)}

					{switcherPosition === 'left' && (
						<LocaleSwitcher visible={isSwitcherVisible} position='left' />
					)}

					{!isForm && !!links && <NavbarLinks links={links} />}

					{switcherPosition === 'center' && (
						<Flex flex={1} justifyContent='center' minW={0}>
							<LocaleSwitcher visible={isSwitcherVisible} position='center' />
						</Flex>
					)}

					<Flex gap={{ base: 2, md: 4 }} ms='auto' alignItems='center'>
						{switcherPosition === 'right' && (
							<LocaleSwitcher visible={isSwitcherVisible} position='right' />
						)}
						{/* TODO: localization for both redirect target & login form */}
						{/* Same config.chrome.login opt-out as NavbarLanding — a tenant
						    without a customer portal must not show Login anywhere. */}
						{isLanding && tenantChrome.login && (
							<Button
								as={Link}
								href={login.href}
								variant='solid'
								size={{ base: 'sm', md: 'md' }}
								flexShrink={0}
								onClick={handleLoginClick}
							>
								{login.signedIn ? t('dashboard') : t('login')}
							</Button>
						)}
					</Flex>
				</Flex>
			</Container>
		</Flex>
	)
}

export default Navbar
